import { type Column, type Table } from "@tanstack/react-table";
import { ChevronDownIcon, LockIcon, SettingsIcon } from "lucide-react";

import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";

type DataTableViewOptionsProps<TData> = {
  table: Table<TData>;
  className?: string;
  buttonSize?: React.ComponentProps<typeof Button>["size"];
  iconOnly?: boolean;
  responsiveSheet?: boolean;
  showReset?: boolean;
  labels?: Partial<{
    trigger: string;
    active: string;
    available: string;
    empty: string;
    reset: string;
  }>;
};

function prettifyLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function columnToggleLabel<TData>(column: Column<TData>): string {
  const header = column.columnDef.header;
  if (typeof header === "string") return header;
  const label = column.columnDef.meta?.label;
  if (typeof label === "string") return label;
  return prettifyLabel(column.id);
}

export function DataTableViewOptions<TData>({
  table,
  className,
  buttonSize = "default",
  iconOnly = false,
  responsiveSheet: _responsiveSheet = false,
  showReset = false,
  labels,
}: DataTableViewOptionsProps<TData>) {
  void _responsiveSheet
  const resolvedLabels = {
    trigger: labels?.trigger ?? "Columns",
    active: labels?.active ?? "Active columns",
    available: labels?.available ?? "Available columns",
    empty: labels?.empty ?? "No active columns.",
    reset: labels?.reset ?? "Reset",
  };
  const allColumns = table.getAllColumns().filter((column) => {
    const meta = column.columnDef.meta as { hidden?: boolean } | undefined;
    if (meta?.hidden) return false;
    return column.getCanHide() || column.getIsVisible();
  });
  const activeColumns = allColumns.filter((column) => column.getIsVisible());
  const availableColumns = allColumns.filter((column) => !column.getIsVisible());

  const content = (
    <>
      <div className='w-full min-w-0 space-y-1 p-2'>
        <p className='px-1 text-start text-xs font-medium text-muted-foreground'>{resolvedLabels.active}</p>
        {activeColumns.length ? (
          activeColumns.map((column) => {
            const canHide = column.getCanHide();
            const handleHide = () => {
              if (canHide) {
                column.toggleVisibility(false);
              }
            };
            return (
              <div
                key={column.id}
                className='flex w-full min-w-0 items-center justify-start gap-2 self-stretch rounded-md px-1.5 py-1.5 text-left text-sm hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60'
                role={canHide ? "button" : undefined}
                tabIndex={canHide ? 0 : -1}
                aria-disabled={!canHide}
                onClick={handleHide}
                onKeyDown={(event) => {
                  if (!canHide) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleHide();
                  }
                }}
              >
                <Checkbox
                  checked
                  disabled={!canHide}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={(checked) => {
                    if (canHide) {
                      column.toggleVisibility(Boolean(checked));
                    }
                  }}
                />
                <span className='min-w-0 flex-1 truncate text-left'>{columnToggleLabel(column)}</span>
                {!canHide ? <LockIcon className='size-3.5 text-muted-foreground' /> : null}
              </div>
            );
          })
        ) : (
          <p className='px-1 py-1 text-sm text-muted-foreground'>{resolvedLabels.empty}</p>
        )}
      </div>

      {availableColumns.length ? (
        <div className='w-full min-w-0 border-t p-2'>
          <p className='px-1 text-xs font-medium text-muted-foreground'>{resolvedLabels.available}</p>
          <div className='mt-1 w-full min-w-0 space-y-1'>
            {availableColumns.map((column) => {
              const handleShow = () => column.toggleVisibility(true);
              return (
                <div
                  key={column.id}
                  className='flex w-full min-w-0 items-center justify-start gap-2 self-stretch rounded-md px-1.5 py-1.5 text-left text-sm hover:bg-muted/50'
                  role='button'
                  tabIndex={0}
                  onClick={handleShow}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleShow();
                    }
                  }}
                >
                  <Checkbox
                    checked={false}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={(checked) => column.toggleVisibility(Boolean(checked))}
                  />
                  <span className='min-w-0 flex-1 truncate text-left'>{columnToggleLabel(column)}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      {showReset ? (
        <div className='border-t p-2'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='w-full justify-start'
            onClick={() => table.resetColumnVisibility()}
          >
            {resolvedLabels.reset}
          </Button>
        </div>
      ) : null}
    </>
  );

  const trigger = (
    <Button
      type='button'
      variant='ghost'
      size={iconOnly ? "icon" : buttonSize}
      className={cn("shrink-0 [&_>span]:justify-start", className)}
      aria-label={resolvedLabels.trigger}
      title={resolvedLabels.trigger}
    >
      <SettingsIcon />
      {iconOnly ? <span className='sr-only'>{resolvedLabels.trigger}</span> : resolvedLabels.trigger}
      {!iconOnly ? <ChevronDownIcon className='size-4 text-muted-foreground' /> : null}
    </Button>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align='end'
        className='flex w-72 max-w-[min(100vw-1rem,18rem)] flex-col items-stretch p-0 text-start'
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
